import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import type { CutPiece } from "@/hooks/use-pipe-sketch";
import { formatFtIn } from "@/lib/pipe-spec";

interface Totals {
  totalFeet: number;
  totalWeight: number;
  bySize: [string, { feet: number; weight: number; pieces: number }][];
  fittingCounts: [string, number][];
  welds: number;
  pieces: number;
}

export default function PipeCutList({
  cutList,
  totals,
}: {
  cutList: CutPiece[];
  totals: Totals;
}) {
  const copy = () => {
    const lines = [
      "CUT LIST — Carbon Steel A106 Gr.B, Sch 40, BW",
      "",
      ...cutList.map(
        (p, i) =>
          `${i + 1}. ${p.size} — cut ${formatFtIn(p.cutLength)}  (C/C ${formatFtIn(
            p.centerLength,
          )}, ${p.weldEnds} weld end${p.weldEnds === 1 ? "" : "s"})`,
      ),
      "",
      "FITTINGS",
      ...totals.fittingCounts.map(([k, n]) => `${n} × ${k}`),
      "",
      `Total pipe: ${totals.totalFeet.toFixed(2)} ft`,
      `Est. weight: ${totals.totalWeight.toFixed(1)} lb`,
      `Field welds: ${totals.welds}`,
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Cut list copied");
  };

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Cut List</h2>
        <Button size="sm" variant="outline" onClick={copy} disabled={!cutList.length}>
          <Copy className="mr-1 h-3.5 w-3.5" /> Copy
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Cut lengths are center-to-center minus butt-weld takeoffs — 1.5 × NPS for 90° LR elbows,
        B16.9 center-to-end for tees, and reducer end-to-end length.
      </p>

      {!cutList.length && (
        <p className="text-xs text-muted-foreground">
          Click the grid to set a start point, then click along an axis to run pipe. Turns get an
          elbow, size changes get a reducer, and Branch mode tees off an existing run.
        </p>
      )}


      {cutList.length > 0 && (
        <div className="space-y-1">
          {cutList.map((p, i) => (
            <div
              key={`${p.runId}-${p.index}`}
              className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs"
            >
              <span className="font-mono text-muted-foreground">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-medium">{p.size}</span>
              <span className="font-mono">{formatFtIn(p.cutLength)}</span>
              <span className="font-mono text-[10px] text-muted-foreground">
                C/C {formatFtIn(p.centerLength)}
              </span>
            </div>
          ))}
        </div>
      )}

      {totals.fittingCounts.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-semibold">Fittings</h3>
          {totals.fittingCounts.map(([k, n]) => (
            <div key={k} className="flex justify-between text-xs">
              <span>{k}</span>
              <span className="font-mono">{n}</span>
            </div>
          ))}
        </div>
      )}

      {cutList.length > 0 && (
        <div className="space-y-1 border-t border-border pt-2 text-xs">
          {totals.bySize.map(([sz, v]) => (
            <div key={sz} className="flex justify-between text-muted-foreground">
              <span>{sz} pipe</span>
              <span className="font-mono">
                {v.feet.toFixed(2)} ft · {v.weight.toFixed(0)} lb
              </span>
            </div>
          ))}
          <div className="flex justify-between font-semibold">
            <span>Total pipe</span>
            <span className="font-mono">{totals.totalFeet.toFixed(2)} ft</span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>Est. weight</span>
            <span className="font-mono">{totals.totalWeight.toFixed(1)} lb</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>Field welds</span>
            <span className="font-mono">{totals.welds}</span>
          </div>
        </div>
      )}
    </div>
  );
}
